import { useState } from "react";
import { JerseyNumber } from "../../components/rink/JerseyNumber";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { LabelGrid, LabelGridRow } from "../../components/ui/LabelGrid";
import { useI18n } from "../../i18n/I18nProvider";
import type { Person } from "../../lib/api-types";

export interface AddPlayersProps {
  /** Roster people who are not in the lineup yet. */
  available: Person[];
  disabled: boolean;
  onAdd: (personId: string) => void;
}

/**
 * When everyone is in the lineup, only a quiet line. Otherwise one "Add players" button opens the
 * picker: roster people not in the lineup, filtered by a search field, each with an Add button.
 */
export function AddPlayers({ available, disabled, onAdd }: AddPlayersProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  if (available.length === 0) {
    return <p className="text-note text-ink-3">{t("editor.noAvailable")}</p>;
  }
  const shown = filterByName(available, query);

  return (
    <div className="space-y-3">
      <Button
        aria-expanded={open}
        aria-controls="add-players-panel"
        onClick={() => setOpen((value) => !value)}
      >
        {t("editor.addPlayers")}
      </Button>
      {open ? (
        <section
          id="add-players-panel"
          aria-label={t("editor.addPlayers")}
          className="space-y-3 rounded-panel bg-board p-4 shadow-board"
        >
          <Input
            type="search"
            aria-label={t("editor.searchPlayers")}
            placeholder={t("editor.searchPlayers")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="sm:max-w-64"
          />
          {shown.length === 0 ? (
            <p className="text-note text-ink-2">{t("editor.noMatches")}</p>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              <LabelGrid>
                {shown.map((person) => (
                  <LabelGridRow
                    key={person.id}
                    columns="2.5rem minmax(0,1fr) auto"
                    className="py-2"
                  >
                    <JerseyNumber value={person.number ?? "—"} size="sm" />
                    <span className="truncate font-semibold text-ink">{person.name}</span>
                    <Button
                      size="sm"
                      disabled={disabled}
                      aria-label={t("editor.addPlayer", { name: person.name })}
                      onClick={() => onAdd(person.id)}
                    >
                      {t("common.add")}
                    </Button>
                  </LabelGridRow>
                ))}
              </LabelGrid>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}

/** Case-insensitive name match. An empty query keeps everyone. */
export function filterByName(people: readonly Person[], query: string): Person[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === "") return [...people];
  return people.filter((person) => person.name.toLocaleLowerCase().includes(needle));
}
